const TYPE_LABELS: Record<string, string> = {
  adult: 'Взрослый',
  child: 'Детский',
  electric: 'Электро',
  lock: 'Замок',
  childSeat: 'Кресло',
  cart: 'Тележка',
}

export interface InventoryBikeRow {
  id: string
  code: string
  type: string
  status: string
  model?: string | null
  tariff?: { name?: string | null; color?: string | null } | null
}

// Group by tariff (per ТЗ #15) — fall back to model/type only when a bike has no tariff.
function groupLabel(b: InventoryBikeRow): string {
  const tName = b.tariff?.name?.trim()
  if (tName) return tName
  const m = b.model?.trim()
  if (m) return m
  return TYPE_LABELS[b.type] ?? b.type ?? 'Без тарифа'
}

/** В перечне моделей/номеров — только не в активной аренде (по ТЗ). */
const LIST_STATUSES = new Set(['free', 'reserved', 'service'])

function groupBikes(bikes: InventoryBikeRow[]) {
  const map = new Map<string, InventoryBikeRow[]>()
  for (const b of bikes) {
    const key = groupLabel(b)
    const arr = map.get(key) ?? []
    arr.push(b)
    map.set(key, arr)
  }
  for (const arr of map.values()) {
    arr.sort((a, b) => String(a.code).localeCompare(String(b.code), 'ru', { numeric: true }))
  }
  // Order groups by the smallest bike code inside each group, so the list follows
  // the natural code ranges (Детский 1xx, Комфорт 2xx, Прогулочный 5xx, Электро 7xx)
  // instead of alphabetical tariff names (#15).
  const minCode = (rows: InventoryBikeRow[]) =>
    Math.min(...rows.map((r) => parseInt(String(r.code).replace(/\D/g, ''), 10) || Number.MAX_SAFE_INTEGER))
  return Array.from(map.entries()).sort(([, ra], [, rb]) => minCode(ra) - minCode(rb))
}

export default function InventoryAtPointSummary({ bikes }: { bikes: InventoryBikeRow[] }) {
  const free = bikes.filter((b) => b.status === 'free')
  const inUse = bikes.filter((b) => b.status === 'in_use')
  const reserved = bikes.filter((b) => b.status === 'reserved')
  const service = bikes.filter((b) => b.status === 'service')
  const listBikes = bikes.filter((b) => LIST_STATUSES.has(b.status))
  const groups = groupBikes(listBikes)

  return (
    <div className="space-y-1">
      <div className="grid grid-cols-2 gap-2 text-sm">
        <div className="bg-green-50 rounded-xl px-3 py-2 flex justify-between">
          <span className="text-gray-600">Свободно</span>
          <span className="font-bold text-green-700">{free.length}</span>
        </div>
        <div className="bg-blue-50 rounded-xl px-3 py-2 flex justify-between">
          <span className="text-gray-600">В аренде</span>
          <span className="font-bold text-blue-700">{inUse.length}</span>
        </div>
        <div className="bg-yellow-50 rounded-xl px-3 py-2 flex justify-between">
          <span className="text-gray-600">Забронировано</span>
          <span className="font-bold text-yellow-700">{reserved.length}</span>
        </div>
        <div className="bg-orange-50 rounded-xl px-3 py-2 flex justify-between">
          <span className="text-gray-600">Сервис</span>
          <span className="font-bold text-orange-700">{service.length}</span>
        </div>
      </div>
      <div className="bg-gray-50 rounded-xl px-3 py-2 flex justify-between text-sm">
        <span className="text-gray-500">Итого на точке</span>
        <span className="font-bold">{bikes.length}</span>
      </div>
      {groups.length > 0 && (
        <div className="mt-3 space-y-3 text-sm">
          <p className="text-xs font-semibold text-gray-600 uppercase tracking-wide">
            Инвентарь фактически на точке
          </p>
          <p className="text-[11px] text-gray-400 -mt-2 mb-1">
            Свободные, забронированные и в сервисе (без аренды)
          </p>
          {groups.map(([label, list]) => (
            <div key={label}>
              <p className="font-semibold text-gray-800">
                {label}
                <span className="text-gray-500 font-normal"> — {list.length} шт</span>
              </p>
              <div className="flex flex-wrap gap-x-2 gap-y-0.5 mt-1">
                {list.map((b) => (
                  <span key={b.id} className="flex items-center gap-1 text-xs text-gray-600">
                    {b.tariff?.color && (
                      <span style={{ width: 8, height: 8, backgroundColor: b.tariff.color, borderRadius: '50%', display: 'inline-block', flexShrink: 0 }} />
                    )}
                    {b.code}
                  </span>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  )
}
